import { useState } from "react";
import { ChevronDown, ChevronUp, Edit2 } from "lucide-react";
import { motion, AnimatePresence } from "motion/react";
import { SalaryData } from "../../../types/SalaryData";

interface CollapsedSummaryCardProps {
  salaryData: SalaryData;
  onEdit: () => void;
}

interface SummarySection {
  title: string;
  fields: { label: string; value: string }[];
}

export function CollapsedSummaryCard({ salaryData, onEdit }: CollapsedSummaryCardProps) {
  const [expandedSection, setExpandedSection] = useState<number | null>(null);

  const sections: SummarySection[] = [
    {
      title: "Din profil",
      fields: [
        { label: "Situation", value: salaryData.situation || "—" },
        { label: "Jobtitel", value: salaryData.jobTitle || "—" },
      ],
    },
    {
      title: "Din baggrund",
      fields: [
        { label: "Erfaring", value: salaryData.experience !== null ? `${salaryData.experience} år` : "—" },
        { label: "Uddannelse", value: salaryData.education || "—" },
        { label: "Alder", value: salaryData.age || "—" },
      ],
    },
    {
      title: "Din løn og kontekst",
      fields: [
        { label: "Region", value: salaryData.region || "—" },
        { label: "Sektor", value: salaryData.sector || "—" },
        { label: "Ansvarsniveau", value: salaryData.responsibility || "—" },
        { label: "Bruttoløn", value: salaryData.salary > 0 ? `${salaryData.salary.toLocaleString("da-DK")} kr./md.` : "—" },
      ],
    },
  ];

  return (
    <div className="bg-white rounded-2xl shadow-[0_1px_3px_rgba(0,0,0,0.06),0_1px_2px_rgba(0,0,0,0.04)] border border-gray-100/60">
      <div className="px-5 py-4 border-b border-gray-100">
        <div className="flex items-center justify-between">
          <h3 className="text-[13px] font-bold text-[#3A3A39]" style={{marginBottom: "0px"}}>Dine oplysninger</h3>
          <button
            onClick={onEdit}
            className="flex items-center gap-1.5 text-[11px!important] font-medium text-[#008e9f!important] hover:text-[#007585!important] transition-colors cursor-pointer"
          >
            <Edit2 className="w-3 h-3" />
            Redigér alt
          </button>
        </div>
      </div>

      <div className="divide-y divide-gray-100">
        {sections.map((section, index) => (
          <SummaryRow
            key={index}
            section={section}
            isExpanded={expandedSection === index}
            onToggle={() => setExpandedSection(expandedSection === index ? null : index)}
          />
        ))}
      </div>
    </div>
  );
}

function SummaryRow({
  section,
  isExpanded,
  onToggle,
}: {
  section: SummarySection;
  isExpanded: boolean;
  onToggle: () => void;
}) {
  // Get all non-empty values for preview
  const filledFields = section.fields.filter(f => f.value !== "—");
  const preview = filledFields.length > 0
    ? filledFields.map(f => f.value).join(" • ")
    : "—";

  return (
    <div className="px-5 py-3">
      <button
        onClick={onToggle}
        className="w-full flex items-center justify-between text-left group"
      >
        <div className="flex-1 min-w-0">
          <div className="text-[11px] font-bold text-[#7b7b7a] uppercase tracking-wide mb-0.5">
            {section.title}
          </div>
          {!isExpanded && (
            <div className="text-[12px] text-[#3A3A39] font-medium line-clamp-2 leading-relaxed">
              {preview}
            </div>
          )}
        </div>
        <div className="ml-3 text-[#7b7b7a] group-hover:text-[#3A3A39] transition-colors shrink-0">
          {isExpanded ? (
            <ChevronUp className="w-4 h-4" />
          ) : (
            <ChevronDown className="w-4 h-4" />
          )}
        </div>
      </button>

      <AnimatePresence>
        {isExpanded && (
          <motion.div
            key="expanded-content"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2, ease: "easeOut" }}
            className="overflow-hidden"
          >
            <div className="pt-3 space-y-2">
              {section.fields.map((field, i) => (
                <div key={i} className="flex items-center justify-between">
                  <span className="text-[11px] text-[#7b7b7a]">{field.label}</span>
                  <span className="text-[12px] font-medium text-[#3A3A39]">{field.value}</span>
                </div>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}